const AVAILABILITY_FILTER = 'filter.v.availability';
const IN_STOCK_VALUE = '1';
const COLLECTION_RETURN_KEY = 'zeus:collection-return';

function applyInStockFilter(root) {
  const url = new URL(window.location.href);
  const current = url.searchParams.get(AVAILABILITY_FILTER);
  const shouldFilter = root.dataset.hideSoldOut === 'true' && root.dataset.autoInStockFilter === 'true';

  if (!shouldFilter) {
    if (current === IN_STOCK_VALUE) {
      url.searchParams.delete(AVAILABILITY_FILTER);
      url.searchParams.delete('page');
      window.location.replace(url.toString());
    }
    return;
  }

  if (current === IN_STOCK_VALUE) return;

  url.searchParams.set(AVAILABILITY_FILTER, IN_STOCK_VALUE);
  url.searchParams.delete('page');
  window.location.replace(url.toString());
}

function collectionStateKey() {
  const url = new URL(window.location.href);
  url.searchParams.delete('page');
  return `${url.pathname}${url.search}`;
}

function readCollectionReturnState() {
  const raw = sessionStorage.getItem(COLLECTION_RETURN_KEY);
  if (!raw) return null;

  try {
    return JSON.parse(raw);
  } catch {
    sessionStorage.removeItem(COLLECTION_RETURN_KEY);
    return null;
  }
}

function clearCollectionReturnState() {
  sessionStorage.removeItem(COLLECTION_RETURN_KEY);
}

function isBackForwardNavigation() {
  const nav = performance.getEntriesByType('navigation')[0];
  if (nav?.type) {
    return nav.type === 'back_forward';
  }

  return performance.navigation?.type === 2;
}

function saveCollectionReturnState(root) {
  const loadMore = root.querySelector('[data-load-more]');

  sessionStorage.setItem(
    COLLECTION_RETURN_KEY,
    JSON.stringify({
      key: collectionStateKey(),
      scrollY: window.scrollY,
      pagesLoaded: parseInt(root.dataset.pagesLoaded || '1', 10),
      nextUrl: loadMore?.dataset.nextUrl || null,
    })
  );
}

class CollectionFilters {
  constructor(root) {
    this.root = root;
    this.drawer = root.querySelector('[data-filter-drawer]');
    this.openButton = root.querySelector('[data-filter-open]');
    this.closeTargets = root.querySelectorAll('[data-filter-close]');
    this.sortSelect = root.querySelector('[data-collection-sort]');
    this.forms = root.querySelectorAll('.collection-filters-form');

    this.bind();
  }

  bind() {
    if (this.openButton && this.drawer) {
      this.openButton.addEventListener('click', () => this.openDrawer());
    }

    this.closeTargets.forEach((el) => {
      el.addEventListener('click', () => this.closeDrawer());
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') this.closeDrawer();
    });

    if (this.sortSelect) {
      this.sortSelect.addEventListener('change', () => this.onSortChange());
    }

    this.forms.forEach((form) => {
      form.querySelectorAll('input[type="checkbox"]').forEach((input) => {
        input.addEventListener('change', () => form.submit());
      });
    });
  }

  openDrawer() {
    if (!this.drawer) return;

    this.drawer.classList.add('is-open');
    this.drawer.setAttribute('aria-hidden', 'false');

    if (this.openButton) {
      this.openButton.setAttribute('aria-expanded', 'true');
    }

    document.body.style.overflow = 'hidden';
  }

  closeDrawer() {
    if (!this.drawer) return;

    this.drawer.classList.remove('is-open');
    this.drawer.setAttribute('aria-hidden', 'true');

    if (this.openButton) {
      this.openButton.setAttribute('aria-expanded', 'false');
    }

    document.body.style.overflow = '';
  }

  onSortChange() {
    const url = new URL(window.location.href);
    url.searchParams.set('sort_by', this.sortSelect.value);
    url.searchParams.delete('page');

    if (this.root.dataset.hideSoldOut === 'true' && this.root.dataset.autoInStockFilter === 'true') {
      url.searchParams.set(AVAILABILITY_FILTER, IN_STOCK_VALUE);
    } else {
      url.searchParams.delete(AVAILABILITY_FILTER);
    }

    window.location.href = url.toString();
  }
}

class CollectionLoadMore {
  constructor(root) {
    this.root = root;
    this.grid = root.querySelector('[data-collection-product-grid]');
    this.loadMore = root.querySelector('[data-load-more]');
    this.button = root.querySelector('[data-load-more-btn]');
    this.status = root.querySelector('[data-load-more-status]');
    this.sectionId = root.dataset.sectionId;
    this.loading = false;
    this.restoring = false;

    this.bind();
    this.restoreOnReturn();
  }

  bind() {
    if (this.button && this.loadMore) {
      this.button.addEventListener('click', () => this.onLoadMoreClick());
    }

    this.root.addEventListener(
      'click',
      (event) => {
        if (event.defaultPrevented) return;
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        if (event.target.closest('a.product-tile')) {
          saveCollectionReturnState(this.root);
        }
      },
      true
    );
  }

  async onLoadMoreClick() {
    if (!this.loadMore?.dataset.nextUrl || this.loading) return;
    await this.appendPage(this.loadMore.dataset.nextUrl);
  }

  async appendPage(pageUrl, { silent = false } = {}) {
    if (!pageUrl || !this.grid || !this.sectionId) return false;

    this.loading = true;
    if (!silent && this.button) {
      this.button.disabled = true;
      this.button.textContent = this.button.dataset.loadingLabel || 'Loading…';
    }
    if (!silent && this.status) this.status.textContent = '';

    try {
      const fetchUrl = new URL(pageUrl, window.location.origin);
      fetchUrl.searchParams.set('section_id', this.sectionId);

      const response = await fetch(fetchUrl.toString());
      if (!response.ok) throw new Error('Failed to load more products');

      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const section = doc.querySelector('[data-collection-luxury]');
      const newGrid = section?.querySelector('[data-collection-product-grid]');

      if (!newGrid || newGrid.children.length === 0) {
        throw new Error('No products returned');
      }

      [...newGrid.children].forEach((tile) => {
        this.grid.appendChild(tile);
      });

      const pagesLoaded = parseInt(this.root.dataset.pagesLoaded || '1', 10) + 1;
      this.root.dataset.pagesLoaded = String(pagesLoaded);

      const newLoadMore = section.querySelector('[data-load-more]');
      if (newLoadMore?.dataset.nextUrl) {
        this.loadMore.dataset.nextUrl = newLoadMore.dataset.nextUrl;
      } else if (this.loadMore) {
        this.loadMore.remove();
        this.loadMore = null;
        this.button = null;
      }

      return true;
    } catch (error) {
      if (!silent && this.status) {
        this.status.textContent = 'Could not load more pieces. Please try again.';
      }
      return false;
    } finally {
      this.loading = false;
      if (!silent && this.button) {
        this.button.disabled = false;
        this.button.textContent = this.button.dataset.defaultLabel || 'Load more';
      }
    }
  }

  async restoreOnReturn() {
    const state = readCollectionReturnState();
    if (!state || state.key !== collectionStateKey()) return;

    if (!isBackForwardNavigation()) {
      clearCollectionReturnState();
      return;
    }

    clearCollectionReturnState();
    this.restoring = true;

    const pagesLoaded = parseInt(state.pagesLoaded || '1', 10);
    if (pagesLoaded > 1) {
      const baseUrl = new URL(window.location.href);

      for (let page = 2; page <= pagesLoaded; page += 1) {
        baseUrl.searchParams.set('page', String(page));
        const loaded = await this.appendPage(baseUrl.toString(), { silent: true });
        if (!loaded) break;
      }
    }

    const scrollTarget = Number.isFinite(state.scrollY) ? state.scrollY : 0;
    requestAnimationFrame(() => {
      window.scrollTo(0, scrollTarget);
      this.restoring = false;
    });
  }
}

window.addEventListener('pageshow', (event) => {
  if (event.persisted) {
    clearCollectionReturnState();
  }
});

document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('[data-collection-luxury]').forEach((root) => {
    applyInStockFilter(root);
    new CollectionFilters(root);
    new CollectionLoadMore(root);
  });
});
